import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { buildApiUrl, buildAuthHeaders } from '../config/api';
import './Kitchen.css'; // Import the updated CSS

const ORDER_STATUSES = ['Pending', 'In Progress', 'Ready for Delivery'];

function Kitchen() {
    const [orders, setOrders] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState('');
    const [updatingOrder, setUpdatingOrder] = useState(null);
    const [selectedDriver, setSelectedDriver] = useState({});
    const seenPendingOrderIds = useRef(new Set());
    const hasLoadedOrders = useRef(false);

    const formatDate = (isoString) => {
        const date = new Date(isoString);
        return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
    };

    const flagNewOrders = useCallback((nextOrders) => {
        const newOrders = (nextOrders || []).filter(order => order.status === "Pending");
        const unseenOrders = newOrders.filter(order => !seenPendingOrderIds.current.has(order.id));

        newOrders.forEach(order => seenPendingOrderIds.current.add(order.id));

        if (!hasLoadedOrders.current) {
            hasLoadedOrders.current = true;
            return;
        }

        if (unseenOrders.length > 0) {
            setNotice(`${unseenOrders.length} new pending order${unseenOrders.length === 1 ? '' : 's'} received.`);
        }
    }, []);

    const fetchOrders = useCallback(() => {
        setLoading(true);
        fetch(buildApiUrl('/api/orders'), {
            headers: buildAuthHeaders(),
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to fetch orders');
                }

                return response.json();
            })
            .then(data => {
                setOrders(data || []);
                flagNewOrders(data || []);
                setError(null);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching orders:', error);
                setError('Failed to fetch orders.');
                setLoading(false);
            });
    }, [flagNewOrders]);

    const fetchDrivers = useCallback(() => {
        fetch(buildApiUrl('/api/drivers'), {
            headers: buildAuthHeaders(),
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to fetch drivers');
                }

                return response.json();
            })
            .then(data => setDrivers(data || []))
            .catch(error => console.error('Error fetching drivers:', error));
    }, []);

    useEffect(() => {
        fetchOrders();
        fetchDrivers();

        const interval = setInterval(fetchOrders, 30000);
        return () => clearInterval(interval);
    }, [fetchOrders, fetchDrivers]);

    function updateOrderWithDriver(orderId, driverId) {
        setUpdatingOrder(orderId);

        fetch(buildApiUrl(`/api/orders/${orderId}/assign-driver`), {
            method: "PUT",
            headers: buildAuthHeaders({
                "Content-Type": "application/json",
            }),
            body: JSON.stringify({ driverId }),
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to allocate driver');
                }

                return response.json();
            })
            .then(() => {
                fetchOrders(); // Refresh the orders list
                setUpdatingOrder(null); // Clear the updating state
            })
            .catch(error => {
                console.error('Error updating order with driver:', error);
                setError('Failed to update order with driver.');
                setUpdatingOrder(null); // Clear the updating state
            });
    }

    function updateStatus(orderId, newStatus) {
        setUpdatingOrder(orderId);

        fetch(buildApiUrl(`/api/orders/${orderId}`), {
            method: "PUT",
            headers: buildAuthHeaders({
                "Content-Type": "application/json",
            }),
            body: JSON.stringify({ status: newStatus }),
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to update status');
                }
                return response.json();
            })
            .then(data => {
                // Log the updated order for debugging
                console.log('Order updated successfully:', data);
                fetchOrders(); // Refresh the orders list
                setUpdatingOrder(null); // Clear the updating state
            })
            .catch(error => {
                console.error('Error updating order status:', error);
                setError('Failed to update order status.');
                setUpdatingOrder(null); // Clear the updating state
            });
    }

    function handleDriverChange(orderId, driverId) {
        setSelectedDriver(prevState => ({ ...prevState, [orderId]: driverId }));
    }

    function handleAllocateClick(orderId) {
        const driverId = selectedDriver[orderId];
        if (driverId) {
            updateOrderWithDriver(orderId, driverId);
            setSelectedDriver(prevState => ({ ...prevState, [orderId]: null }));
        } else {
            setError('Select a driver before allocating this order.');
        }
    }

    function handleStatusUpdateClick(orderId, newStatus) {
        updateStatus(orderId, newStatus);
    }

    function renderOrdersByStatus(status) {
        const filteredOrders = (orders || [])
            .filter(order => order.status === status)
            .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

        return (
            <div className="order-section" key={status}>
                <div className="status-heading">
                    <h3>{status}</h3>
                    <span>{filteredOrders.length}</span>
                </div>
                {filteredOrders.length === 0 ? (
                    <p className="kitchen-empty">No orders in this stage.</p>
                ) : (
                    <ul className="order-list">
                    {filteredOrders.map(order => {
                        const assignedDriver = drivers.find(driver => driver.id === order.driverId);
                        const isDriverAssigned = !!order.driverId;
                        return (
                            <li key={order.id} className="order-item">
                                <div className="order-card-header">
                                    <div>
                                        <p className="order-id">Order #{order.id}</p>
                                        <p>{formatDate(order.createdAt)}</p>
                                    </div>
                                    <strong>R{order.total}</strong>
                                </div>

                                <div className="order-detail-grid">
                                    <p><strong>Payment</strong>{order.paymentMethod}</p>
                                    <p><strong>Time slot</strong>{order.deliveryAddress?.deliveryTime || 'ASAP'}</p>
                                    <p><strong>Customer</strong>{order.deliveryAddress?.name || 'N/A'}</p>
                                    <p><strong>Phone</strong>{order.deliveryAddress?.phone || 'N/A'}</p>
                                </div>

                                <div className="order-block">
                                    <strong>Items</strong>
                                    <ul>
                                        {order.items.map((item, index) => (
                                            <li key={`${order.id}-${index}`}>
                                                {item.name} {item.variant?.name && `- ${item.variant.name}`} {item.size && `(${item.size})`} x{item.quantity}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="order-block">
                                    <strong>Delivery</strong>
                                    <p>{order.deliveryAddress ? `${order.deliveryAddress.streetNumber} ${order.deliveryAddress.streetName}, ${order.deliveryAddress.suburb}, ${order.deliveryAddress.postalCode}` : 'N/A'}</p>
                                    {order.deliveryAddress?.deliveryNote && <p>{order.deliveryAddress.deliveryNote}</p>}
                                </div>

                                {status === "Pending" && (
                                    <button
                                        onClick={() => handleStatusUpdateClick(order.id, "In Progress")}
                                        disabled={updatingOrder === order.id}
                                    >
                                        {updatingOrder === order.id ? "Updating..." : "Start Preparing"}
                                    </button>
                                )}

                                {status === "In Progress" && (
                                    <button
                                        onClick={() => handleStatusUpdateClick(order.id, "Ready for Delivery")}
                                        disabled={updatingOrder === order.id}
                                    >
                                        {updatingOrder === order.id ? "Updating..." : "Ready for Delivery"}
                                    </button>
                                )}

                                {status === "Ready for Delivery" && !isDriverAssigned && (
                                    <>
                                        <label htmlFor={`driver-select-${order.id}`}>Assign Driver:</label>
                                        <select
                                            id={`driver-select-${order.id}`}
                                            value={selectedDriver[order.id] || ''}
                                            onChange={e => handleDriverChange(order.id, e.target.value)}
                                        >
                                            <option value="">Select a driver</option>
                                            {(drivers || []).map(driver => (
                                                <option key={driver.id} value={driver.id}>
                                                    {driver.name}
                                                </option>
                                            ))}
                                        </select>
                                        <button
                                            onClick={() => handleAllocateClick(order.id)}
                                            disabled={updatingOrder === order.id}
                                        >
                                            {updatingOrder === order.id ? "Allocating..." : "Allocate to Driver"}
                                        </button>
                                    </>
                                )}

                                {status === "Ready for Delivery" && isDriverAssigned && (
                                    <p><strong>Assigned Driver:</strong> {assignedDriver ? assignedDriver.name : 'Unknown'}</p>
                                )}

                                {status === "Delivered" && (
                                    <p><strong>Assigned Driver:</strong> {assignedDriver ? assignedDriver.name : 'Unknown'}</p>
                                )}
                            </li>
                        );
                    })}
                    </ul>
                )}
            </div>
        );
    }

    const statusCounts = useMemo(() => ORDER_STATUSES.reduce((accumulator, status) => {
        accumulator[status] = orders.filter(order => order.status === status).length;
        return accumulator;
    }, {}), [orders]);

    return (
        <div className="kitchen-orders">
            <div className="kitchen-header">
                <div>
                    <p className="eyebrow">Operations</p>
                    <h2>Kitchen orders</h2>
                    <p>Live view for preparation, allocation, and dispatch.</p>
                </div>
                <div className="operations-actions">
                    <Link to="/driver-orders">Dispatch</Link>
                    <Link to="/update-menu">Menu manager</Link>
                    <Link to="/menu-availability">Sold out</Link>
                    <Link to="/business-closures">Closures</Link>
                    <button type="button" onClick={fetchOrders} disabled={loading}>{loading ? 'Refreshing...' : 'Refresh'}</button>
                </div>
            </div>

            {notice && (
                <button type="button" className="kitchen-notice" onClick={() => setNotice('')}>
                    {notice}
                </button>
            )}

            <div className="kitchen-summary">
                {ORDER_STATUSES.map(status => (
                    <div key={status}>
                        <span>{status}</span>
                        <strong>{statusCounts[status] || 0}</strong>
                    </div>
                ))}
            </div>

            {loading ? (
                <p className="kitchen-empty">Loading live orders...</p>
            ) : error ? (
                <p className="error-message">{error}</p>
            ) : (
                <>
                    {ORDER_STATUSES.map(renderOrdersByStatus)}
                </>
            )}
        </div>
    );
}

export default Kitchen;
